/**
 * Level of detail for the starmap.
 *
 * There are no separate galaxy/cluster/system screens: it is one continuous
 * space, and which tier you are looking at is purely a function of how close
 * the camera has got to something. Flying in far enough resolves a cluster into
 * stars, and a star into a system.
 *
 * ── Scale ───────────────────────────────────────────────────────────────────
 *
 * A system's contents span tens of thousands of units, while its neighbours sit
 * about a thousand away — so system contents are drawn at `SYSTEM_RENDER_SCALE`
 * when embedded in galactic space. Without that, entering a system would put
 * its outer planets inside the next system along.
 *
 * The tiers overlap deliberately, with hysteresis, so drifting across a
 * boundary cannot flicker between two builds.
 *
 * Deliberately free of any Three.js import: this is arithmetic on positions, so
 * it stays testable without a renderer — which matters, because a headless
 * browser never runs the render loop that would otherwise exercise it.
 */

import { getGalaxyMap, type Cluster, type SystemSummary, type Vec3 } from '@interspacial/shared';

export type Tier = 'galaxy' | 'cluster' | 'system';

/** System contents are drawn this much smaller when placed in the galaxy. */
export const SYSTEM_RENDER_SCALE = 0.035;

/**
 * How close the *view* has to be for a tier to resolve, as a multiple of the
 * focused cluster's radius. This is view distance — pivot to camera — not
 * distance to the cluster: the pivot picks the subject, zoom picks the detail.
 */
export const CLUSTER_ENTER = 2.4;
const CLUSTER_EXIT = 3.1;

/** View distances in galactic units for resolving a single system. */
const SYSTEM_ENTER = 620;
export const SYSTEM_EXIT = 900;

/**
 * How near the pivot must be to something to make it the subject at all.
 * Beyond this the pivot is in open space and there is nothing to resolve.
 */
const CLUSTER_CAPTURE = 1.25;
const SYSTEM_CAPTURE = 900;

export interface Focus {
  tier: Tier;
  cluster: Cluster | null;
  system: SystemSummary | null;
}

function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

/**
 * Tracks what the camera is close enough to see, and answers with the tier plus
 * whichever cluster and system are in focus.
 */
interface ClusterEntry {
  cluster: Cluster;
  systems: readonly SystemSummary[];
}

export class LodTracker {
  #entries: ClusterEntry[];
  #current: Focus = { tier: 'galaxy', cluster: null, system: null };
  #heightRange: { min: number; max: number };

  constructor(seed: string) {
    this.#entries = getGalaxyMap(seed).map(({ cluster, systems }) => ({ cluster, systems }));

    let min = Infinity;
    let max = -Infinity;
    for (const { cluster } of this.#entries) {
      min = Math.min(min, cluster.position.z - cluster.radius);
      max = Math.max(max, cluster.position.z + cluster.radius);
    }
    this.#heightRange = { min, max };
  }

  get focus(): Focus {
    return this.#current;
  }

  /**
   * How far above and below the galactic plane the clusters actually reach.
   * The pivot is clamped to this so it cannot wander off into empty space above
   * the disc, where there is nothing to look at and no way to tell where you are.
   */
  get heightRange(): { min: number; max: number } {
    return this.#heightRange;
  }

  #nearestCluster(camera: Vec3): { entry: ClusterEntry; distance: number } | null {
    let best: ClusterEntry | null = null;
    let bestDistance = Infinity;

    for (const entry of this.#entries) {
      const d = distance(camera, entry.cluster.position);
      if (d < bestDistance) {
        bestDistance = d;
        best = entry;
      }
    }
    return best ? { entry: best, distance: bestDistance } : null;
  }

  static #nearestSystem(
    systems: readonly SystemSummary[],
    camera: Vec3,
  ): { system: SystemSummary; distance: number } | null {
    let best: SystemSummary | null = null;
    let bestDistance = Infinity;

    for (const system of systems) {
      const d = distance(camera, system.galacticPosition);
      if (d < bestDistance) {
        bestDistance = d;
        best = system;
      }
    }
    return best ? { system: best, distance: bestDistance } : null;
  }

  /** Every cluster, for screen-space snapping. */
  get clusters(): readonly Cluster[] {
    return this.#entries.map((entry) => entry.cluster);
  }

  /** The systems of one cluster, for snapping around inside it. */
  systemsOf(clusterId: string): readonly SystemSummary[] {
    return this.#entries.find((entry) => entry.cluster.id === clusterId)?.systems ?? [];
  }

  /** Nearest cluster to a point, if the point is near enough to capture it. */
  clusterNear(pivot: Vec3): Cluster | null {
    const nearest = this.#nearestCluster(pivot);
    if (!nearest) return null;
    return nearest.distance <= nearest.entry.cluster.radius * CLUSTER_CAPTURE
      ? nearest.entry.cluster
      : null;
  }

  /**
   * Recompute focus.
   *
   * Two separate inputs, deliberately: the **pivot** decides *what* is in
   * focus, and the **view distance** decides *how much detail* it gets. Keying
   * everything off the camera instead would mean flying past a cluster
   * resolved it, and zooming in on the thing you had centred did not.
   *
   * Returns true when the focus changed enough that the scene needs rebuilding
   * — the caller uses that to avoid tearing down geometry every frame.
   */
  update(pivot: Vec3, viewDistance: number): boolean {
    const nearest = this.#nearestCluster(pivot);
    if (!nearest) return false;

    const { entry, distance: pivotToCluster } = nearest;
    const previous = this.#current;

    // The pivot has to actually be on the cluster for it to be the subject.
    if (pivotToCluster > entry.cluster.radius * CLUSTER_CAPTURE) {
      return this.#commit({ tier: 'galaxy', cluster: null, system: null });
    }

    // Hysteresis: the threshold to leave is further out than the one to enter,
    // so hovering exactly on a boundary cannot oscillate.
    const alreadyHere = previous.cluster?.id === entry.cluster.id;
    const clusterThreshold = entry.cluster.radius * (alreadyHere ? CLUSTER_EXIT : CLUSTER_ENTER);

    if (viewDistance >= clusterThreshold) {
      return this.#commit({ tier: 'galaxy', cluster: null, system: null });
    }

    const nearestSystem = LodTracker.#nearestSystem(entry.systems, pivot);
    if (nearestSystem && nearestSystem.distance <= SYSTEM_CAPTURE) {
      const sameSystem = previous.system?.id === nearestSystem.system.id;
      const systemThreshold = sameSystem ? SYSTEM_EXIT : SYSTEM_ENTER;

      if (viewDistance < systemThreshold) {
        return this.#commit({
          tier: 'system',
          cluster: entry.cluster,
          system: nearestSystem.system,
        });
      }
    }

    return this.#commit({ tier: 'cluster', cluster: entry.cluster, system: null });
  }

  #commit(next: Focus): boolean {
    const changed =
      next.tier !== this.#current.tier ||
      next.cluster?.id !== this.#current.cluster?.id ||
      next.system?.id !== this.#current.system?.id;

    this.#current = next;
    return changed;
  }
}
